import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomInOutlined';
import { Box, IconButton, SxProps, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { DRAWER_IMAGE_HEIGHT_DESKTOP, DRAWER_IMAGE_MAX_HEIGHT_MOBILE, HOVER_MEDIA } from '../../../constants/ui';
import { IVariantOptionRow } from '../../../types/marketplace';
import { ICampaignItem } from '../../../types/pyg';
import { DescriptionSourceEnum } from '../../../types/shell';
import { hasDescriptionContent } from '../../../util/richText';
import { DrawerDescription, OptionNameLine, QuantityChip, ShipsFromNote, VendorLine } from '../_components_';
import LightboxBackgroundImage from '../_components_/LightboxBackgroundImage';
import { ThumbnailRail } from '../_components_/ThumbnailRail';
import { VariantOptionRows } from '../_components_/VariantOptionRows';

// Revealed on hover over the image; the frame's hover rule toggles opacity and pointer events.
const RevealOnHoverButton = styled(IconButton)(({ theme: { palette, transitions } }) => ({
  position: 'absolute',
  padding: 0,
  opacity: 0,
  pointerEvents: 'none',
  backgroundColor: palette.scrim.zoomButton,
  color: palette.action.active,
  transition: transitions.create(['opacity', 'background-color'], { duration: transitions.duration.shorter }),
  [HOVER_MEDIA]: {
    '&:hover': {
      backgroundColor: palette.common.white,
    },
  },
}));

const ZoomInIconContainer = styled(RevealOnHoverButton)(({ theme }) => ({
  right: theme.spacing(1),
  bottom: theme.spacing(1),
  width: 32,
  height: 32,
  borderRadius: theme.shape.borderRadius,
  // Matches the image beneath it, so the cursor does not flip when crossing onto the button.
  cursor: 'zoom-in',
}));

const SliderArrow = styled(RevealOnHoverButton)(() => ({
  top: '50%',
  transform: 'translateY(-50%)',
  width: 36,
  height: 36,
}));

interface IProps {
  item: ICampaignItem;
  simple?: boolean;
  optionName?: string;
  hideName?: boolean;
  optionRows?: IVariantOptionRow[];
  images?: string[];
  vendor?: string;
  description?: string;
  descriptionSource: DescriptionSourceEnum;
  isMarketplace?: boolean;
}

const columnSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 2 };
const headingSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 0.5 };
const nameRowSx: SxProps<Theme> = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 };
const chipAloneSx: SxProps<Theme> = { ml: 'auto' };

const imageCounterSx: SxProps<Theme> = {
  position: 'absolute',
  bottom: 1,
  left: '50%',
  transform: 'translateX(-50%)',
  px: 1,
  borderRadius: 0.5,
  backgroundColor: ({ palette }) => alpha(palette.common.black, 0.6),
  color: 'common.white',
};

const quantityChipSx: SxProps<Theme> = { position: 'absolute', top: 2, right: 2 };

export const DrawerItem: React.FC<IProps> = ({
  item,
  simple = false,
  optionName,
  hideName = false,
  optionRows = [],
  images = [],
  vendor,
  description,
  descriptionSource,
  isMarketplace = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { image_url, name, id, quantity } = item;
  const resolvedDescription = description ?? item.description;
  const hasDescription = hasDescriptionContent(resolvedDescription, descriptionSource);
  const imageRef = useRef<HTMLImageElement>(null);
  const [pickedImage, setPickedImage] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<string[]>([]);

  const handleImageError = useCallback((failedUrl: string) => {
    setFailedImages((failed) => (failed.includes(failedUrl) ? failed : [...failed, failedUrl]));
  }, []);

  const gallery = useMemo(
    () => (images.length ? images : [image_url]).filter((url) => !!url && !failedImages.includes(url)),
    [images, image_url, failedImages],
  );
  const hasImage = gallery.length > 0;
  const displayedImage = pickedImage && gallery.includes(pickedImage) ? pickedImage : gallery[0];

  const activeIndex = Math.max(0, gallery.indexOf(displayedImage));
  const hasSlider = gallery.length > 1;
  // `simple` is the variant-picking view, where the chip would read as stock rather than quantity.
  const isQuantityShown = !simple && (quantity ?? 0) > 1;
  const isNameShown = !simple && !hideName;
  // Without an image the chip has no frame to sit on, so the name row carries it even when the
  // name itself is hidden.
  const isRowChipShown = !hasImage && isQuantityShown;
  const isNameRowShown = isNameShown || isRowChipShown;
  const shownOptionName = simple ? undefined : optionName;
  const isBrandShown = isNameShown && isMarketplace && !!vendor;
  const isHeadingShown = isNameRowShown || !!shownOptionName || isBrandShown;

  const stepImage = useCallback(
    (offset: number) => {
      setPickedImage(gallery[(activeIndex + offset + gallery.length) % gallery.length]);
    },
    [gallery, activeIndex],
  );

  const handlePrev = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      stepImage(-1);
    },
    [stepImage],
  );

  const handleNext = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      stepImage(1);
    },
    [stepImage],
  );

  const handleZoomClick = useCallback(() => {
    imageRef.current?.click();
  }, []);

  const frameSx: SxProps<Theme> = useMemo(
    () => ({
      position: 'relative',
      backgroundColor: 'common.white',
      borderRadius: 0.5,
      ...(isMobile ? { maxHeight: DRAWER_IMAGE_MAX_HEIGHT_MOBILE } : { height: DRAWER_IMAGE_HEIGHT_DESKTOP }),
      [HOVER_MEDIA]: {
        '&:hover .cl-drawer-zoom, &:hover .cl-drawer-slider': { opacity: 1, pointerEvents: 'auto' },
      },
    }),
    [isMobile],
  );

  const imageSx: SxProps<Theme> = useMemo(
    () => ({
      width: '100%',
      objectFit: 'contain',
      ...(isMobile ? { height: 'auto', maxHeight: DRAWER_IMAGE_MAX_HEIGHT_MOBILE } : { height: '100%' }),
    }),
    [isMobile],
  );

  return (
    <Box sx={columnSx}>
      {isHeadingShown && (
        <Box sx={headingSx}>
          {isNameRowShown && (
            <Box sx={nameRowSx}>
              {isNameShown && (
                <Typography variant="body1" fontWeight="medium">
                  {name}
                </Typography>
              )}
              {isRowChipShown && <QuantityChip quantity={quantity ?? 0} sx={isNameShown ? undefined : chipAloneSx} />}
            </Box>
          )}
          {shownOptionName && <OptionNameLine name={shownOptionName} variant="body2" color="text.secondary" />}
          {isBrandShown && <VendorLine vendor={vendor} />}
        </Box>
      )}
      {hasImage && (
        <Box sx={frameSx}>
          <LightboxBackgroundImage
            ref={imageRef}
            key={id}
            url={displayedImage}
            gallery={gallery}
            asImage
            alt={name}
            isScalable
            onImageError={handleImageError}
            sx={imageSx}
          />
          {hasSlider && (
            <>
              <SliderArrow
                className="cl-drawer-slider"
                aria-label="Previous image"
                onClick={handlePrev}
                sx={{ left: 1 }}
              >
                <ChevronLeftIcon />
              </SliderArrow>
              <SliderArrow className="cl-drawer-slider" aria-label="Next image" onClick={handleNext} sx={{ right: 1 }}>
                <ChevronRightIcon />
              </SliderArrow>
              <Typography variant="caption" sx={imageCounterSx}>
                {activeIndex + 1} / {gallery.length}
              </Typography>
            </>
          )}
          <ZoomInIconContainer className="cl-drawer-zoom" aria-label="Zoom image" onClick={handleZoomClick}>
            <ZoomInIcon sx={{ fontSize: 20 }} />
          </ZoomInIconContainer>
          {isQuantityShown && <QuantityChip quantity={quantity ?? 0} sx={quantityChipSx} />}
        </Box>
      )}
      {hasImage && <ThumbnailRail images={gallery} activeImage={displayedImage} alt={name} onPick={setPickedImage} />}
      {hasDescription && <DrawerDescription description={resolvedDescription} source={descriptionSource} clamp />}
      <VariantOptionRows rows={optionRows} />
      {isMarketplace && <ShipsFromNote vendor={vendor} />}
    </Box>
  );
};
